import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { RQBJsonLogic } from 'react-querybuilder';

import {
  QueryBuilderFieldGroup,
  QueryBuilderRuleGroupType,
} from '@dotfile/shared/domain';

import {
  formatJsonLogic,
  parseJsonLogic,
  removeExtraneousJsonLogic,
} from './helpers';

/**
 * Hook for handling query builder logic transformation
 *
 * @param options Configuration options
 * @param options.fieldName The name of the form field to store the jsonLogic
 * @param options.fields The fields configuration for the query builder
 * @param options.isLoading Whether the fields are loading
 * @param options.onChange Optional callback when the logic changes
 */
export const useQueryBuilderLogic = ({
  fieldName,
  fields,
  isLoading,
  onChange,
}: {
  fieldName: string;
  fields: QueryBuilderFieldGroup[];
  isLoading: boolean;
  onChange?: (query: QueryBuilderRuleGroupType) => void;
}): {
  value: RQBJsonLogic;
  parsedLogic: QueryBuilderRuleGroupType;
  isLoading: boolean;
  fields: QueryBuilderFieldGroup[];
  hasExtraneousVariables: boolean;
  handleLogicChange: (query: QueryBuilderRuleGroupType) => void;
} => {
  const { setValue, getValues } = useFormContext();

  const jsonLogic = useMemo(
    () => getValues(fieldName) || {},
    [fieldName, getValues],
  );

  const cleanedLogic = removeExtraneousJsonLogic(
    fields,
    jsonLogic as RQBJsonLogic,
  );

  const hasExtraneousVariables = useMemo(() => {
    return JSON.stringify(jsonLogic) !== JSON.stringify(cleanedLogic);
  }, [jsonLogic, cleanedLogic]);

  useEffect(() => {
    // Mark the field as dirty if there are extraneous variables
    if (hasExtraneousVariables) {
      setValue(
        fieldName,
        removeExtraneousJsonLogic(fields, jsonLogic as RQBJsonLogic),
        {
          shouldValidate: false,
          shouldDirty: true,
        },
      );
    }
  }, [hasExtraneousVariables, fieldName, fields, setValue, jsonLogic]);

  const handleLogicChange = (query: QueryBuilderRuleGroupType) => {
    const logic = formatJsonLogic(query);

    setValue(fieldName, logic || {}, {
      shouldValidate: false,
      shouldDirty: true,
    });

    if (onChange) {
      onChange(query);
    }
  };

  const parsedLogic = parseJsonLogic(cleanedLogic);

  return {
    value: jsonLogic,
    parsedLogic,
    isLoading,
    fields,
    hasExtraneousVariables,
    handleLogicChange,
  };
};
